import { onIdTokenChanged } from "firebase/auth";
import { doc, getDoc, runTransaction, setDoc } from "firebase/firestore";
import { useAccountStore, type SyncState } from "../account/accountStore";
import { PLAYERS } from "../account/playerDoc";
import { auth, db } from "../net/firebase";
import { mergeProfiles } from "./mergeProfiles";
import {
  fromCloudDoc,
  getInstallId,
  isProfileEmpty,
  loadProfile,
  onProfileChange,
  PROFILE_SCHEMA_VERSION,
  saveProfile,
  toCloudDoc,
  type CloudProfileDoc,
  type PlayerProfile,
} from "./profile";
import { syncChoiceKey } from "./syncChoice";

/**
 * The cloud save engine — `docs/CLOUD_SAVES_DESIGN.md` §5, LIVE_SERVICES_PLAN
 * §B4-B7.
 *
 * One operation does all the work: read `players/{uid}/private/profile` inside a
 * transaction, `mergeProfiles(local, cloud)`, write the result to both sides.
 * There is no operation log, no offline queue and no ordering to get right,
 * because the merge is commutative, associative and idempotent (see
 * mergeProfiles.ts) — a retried transaction, two devices committing at once, or
 * the same state arriving twice all converge on the same document.
 *
 * What that leaves this module responsible for is the *when*, and the two states
 * a merge cannot resolve on its own:
 *
 *   - a cloud document from a NEWER build, which we must not write back in our
 *     narrower shape (§B5) — read-only until the app is updated,
 *   - the first sign-in on a device that already has progress, against an
 *     account that also has some (§B7) — a question for the player, asked once
 *     per (account, install) and never again.
 *
 * Everything here is a no-op while signed out or anonymous. An anonymous player
 * has no `players` doc, no rule that would let them write one, and their
 * progress already lives in localStorage.
 */

const PRIVATE = "private";
const PROFILE_DOC = "profile";
/** The §A3 install registry: which installs have synced this account, and when. */
const SYNC_DOC = "sync";

/** §B6: a profile mutation buys a write this far in the future, not immediately. */
const DEBOUNCE_MS = 10_000;

/**
 * What the player answered at the §B7 prompt. Stored per account so the question
 * is asked once per (account, install) — which of the three they picked only
 * matters at the moment they pick it, but recording the answer rather than a
 * bare "asked" flag keeps the key readable in devtools when a sync is being
 * debugged on someone's device.
 */
type SyncChoice = "merge" | "keepCloud" | "keepLocal";

/** How the sync operation should treat the cloud document it finds. */
type SyncMode = "merge" | "overwrite";

/** Aborts the sync transaction on the §B5 version gate; never escapes this module. */
class NewerCloudSchemaError extends Error {}

let started = false;
/** The account currently syncing: null while signed out, anonymous, or resolving. */
let activeUid: string | null = null;
/** §B5 latch: the cloud is ahead of this build, so nothing may be written until the next sign-in. */
let readOnly = false;
/**
 * True while the engine is writing the merged profile back to localStorage.
 * `saveProfile` notifies every `onProfileChange` listener including ours, so
 * without this the sync's own local write would schedule another sync, which
 * would write the same state again and schedule another.
 */
let applying = false;

/** Idempotent: a second call is ignored rather than stacking a second set of listeners. */
export function startCloudSync(): void {
  if (started) return;
  started = true;

  // Not onAuthStateChanged: a guest linking Google keeps their uid, and that
  // only speaks when the uid changes, so their first sync would wait for a reload.
  onIdTokenChanged(auth, (user) => {
    const uid = user && !user.isAnonymous ? user.uid : null;
    // onIdTokenChanged also fires on hourly token refreshes for an unchanged
    // user; re-running the first-sync path on those would re-ask the §B7
    // question's cheap half and burn a write an hour for nothing.
    if (uid === activeUid) return;

    scheduler.cancel();
    readOnly = false;
    activeUid = uid;
    if (!uid) {
      setSyncState("idle");
      return;
    }
    void firstSync(uid);
  });

  onProfileChange(() => {
    if (applying) return;
    trigger(false);
  });

  // Leaving the page is the last chance to bank a round the debounce window is
  // still holding. Both events fire-and-forget: the SDK takes the write, and
  // whether it reaches Firestore before the tab dies is out of our hands — the
  // next sync merges it either way, which is the point of an idempotent merge.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") trigger(true);
  });
  window.addEventListener("pagehide", () => trigger(true));
  window.addEventListener("online", () => trigger(true));
}

/**
 * Answers the §B7 conflict prompt. The choice is remembered for this account, so
 * this is the only time it is ever asked on this install.
 *
 * "Keep local" is destructive to the cloud document; the account screen owns the
 * confirmation step, this just carries it out.
 *
 * The sync runs directly rather than through the scheduler so the returned
 * promise settles when the choice has actually been carried out — the modal
 * stays up until then.
 */
export async function applyConflictChoice(choice: SyncChoice): Promise<void> {
  const uid = activeUid;
  if (uid) rememberChoice(uid, choice);
  useAccountStore.getState().setPendingConflict(null);
  if (!uid) return;

  if (choice === "keepCloud") {
    await adoptCloud(uid);
    return;
  }
  await syncNow(uid, choice === "keepLocal" ? "overwrite" : "merge");
}

/**
 * Asks for a sync. Immediate by default — a caller reaching for the word "flush"
 * wants the write now (round-over, §B6); pass `false` to only open the debounce
 * window instead.
 *
 * Safe to call from anywhere: it does nothing while signed out, anonymous,
 * read-only, or waiting on the §B7 prompt.
 */
export function requestFlush(immediate = true): void {
  trigger(immediate);
}

/**
 * A sync that the caller can wait for: for something about to be checked
 * against the cloud save by the SERVER (the online crest, whose level is read
 * from it), which would otherwise be checked against the save as it was before
 * this round. Runs outside the scheduler - the merge is a transaction and
 * idempotent, so overlapping a scheduled run costs a write, never a wrong
 * answer. Never throws, and does nothing whenever `requestFlush` would.
 */
export async function flushNow(): Promise<void> {
  const uid = activeUid;
  if (uid === null || readOnly || useAccountStore.getState().pendingConflict !== null) return;
  try {
    await syncNow(uid);
  } catch (err) {
    console.warn("[fiveout] flush before a crest update failed", err);
  }
}

function trigger(immediate: boolean): void {
  if (activeUid === null || readOnly) return;
  // An unanswered conflict means we have not been told what to do with this
  // device's progress yet. Writing anything before that answer would decide it.
  if (useAccountStore.getState().pendingConflict !== null) return;
  scheduler.request(immediate);
}

/**
 * The §B7 conflict moment, evaluated once per (account, install).
 *
 * The prompt is expensive to be wrong about in either direction, so it is only
 * raised when both sides genuinely hold progress. Everything else — an empty
 * local profile, a missing or empty cloud document, or a question already
 * answered on this install — records "merge" and syncs silently, which is what
 * the overwhelming majority of sign-ins do.
 */
async function firstSync(uid: string): Promise<void> {
  if (recordedChoice(uid) !== null) {
    trigger(true);
    return;
  }

  if (isProfileEmpty(loadProfile())) {
    rememberChoice(uid, "merge");
    trigger(true);
    return;
  }

  let cloud: PlayerProfile | null;
  try {
    cloud = await readCloud(uid);
  } catch (err) {
    // Not answerable offline: leave the question unasked and let the next
    // trigger try again, rather than merging on the strength of a failed read.
    console.warn("[fiveout] could not read the cloud profile for the first sync", err);
    setSyncState("error");
    return;
  }
  if (activeUid !== uid) return; // signed out or switched accounts while reading

  if (cloud && !isProfileEmpty(cloud)) {
    useAccountStore.getState().setPendingConflict({});
    return;
  }

  rememberChoice(uid, "merge");
  trigger(true);
}

/**
 * The whole sync operation. `mode` is "merge" everywhere except the §B7 "keep
 * local" answer, which replaces the cloud document outright.
 */
async function syncNow(uid: string, mode: SyncMode = "merge"): Promise<void> {
  setSyncState("syncing");
  const ref = profileRef(uid);

  try {
    const merged = await runTransaction(db, async (tx) => {
      // Read inside the callback: a contended transaction runs this again, and
      // the second attempt has to see whatever the local profile is by then.
      const local = loadProfile();
      const snap = await tx.get(ref);
      const cloud = snap.data() as Partial<CloudProfileDoc> | undefined;

      if (!snap.exists() || !cloud) {
        tx.set(ref, toCloudDoc(local));
        return null;
      }
      // The §B5 gate applies to "keep local" too: the rules reject a write that
      // lowers schemaVersion, so overwriting a newer document would fail as a
      // permission error rather than as the "update the app" state it really is.
      if (num(cloud.schemaVersion) > PROFILE_SCHEMA_VERSION) throw new NewerCloudSchemaError();

      if (mode === "overwrite") {
        tx.set(ref, toCloudDoc(local));
        return null;
      }
      // Local is `a`, so the merge keeps this device's round-id lists (§B2).
      const result = mergeProfiles(local, fromCloudDoc(cloud), Date.now());
      tx.set(ref, toCloudDoc(result));
      return result;
    });

    // Only after the commit: a transaction that retried or failed must not leave
    // its provisional merge on the device.
    if (merged) applyLocally(merged);
    setSyncState("idle");
    void recordInstallSync(uid);
  } catch (err) {
    if (err instanceof NewerCloudSchemaError) {
      // Latched until the next sign-in: nothing this build can do about a newer
      // document, and retrying would only produce the same verdict every 10s.
      readOnly = true;
      scheduler.cancel();
      setSyncState("readOnly");
      return;
    }
    console.error("[fiveout] cloud sync failed", err);
    setSyncState("error");
  }
}

/**
 * The §B7 "keep cloud" answer: the cloud document replaces the local profile and
 * nothing is written back. The device-scoped round-id lists come back empty —
 * they never sync (§B2) — which is the honest reading of "discard what is on
 * this device".
 */
async function adoptCloud(uid: string): Promise<void> {
  setSyncState("syncing");
  try {
    const cloud = await readCloud(uid);
    if (cloud) applyLocally(cloud);
    setSyncState("idle");
    void recordInstallSync(uid);
  } catch (err) {
    console.error("[fiveout] could not adopt the cloud profile", err);
    setSyncState("error");
  }
}

/** The cloud profile as a full profile, or null if this account has never synced. */
async function readCloud(uid: string): Promise<PlayerProfile | null> {
  const snap = await getDoc(profileRef(uid));
  const data = snap.data() as Partial<CloudProfileDoc> | undefined;
  return data ? fromCloudDoc(data) : null;
}

function applyLocally(profile: PlayerProfile): void {
  applying = true;
  try {
    saveProfile(profile);
  } finally {
    applying = false;
  }
}

/**
 * §A3's install registry. Best-effort by design: it is diagnostics, not state
 * anything reads back, and a failure here must not turn a committed sync into a
 * reported error.
 */
async function recordInstallSync(uid: string): Promise<void> {
  try {
    await setDoc(
      doc(db, PLAYERS, uid, PRIVATE, SYNC_DOC),
      { installs: { [getInstallId()]: { lastSyncMs: Date.now() } } },
      { merge: true },
    );
  } catch (err) {
    console.warn("[fiveout] could not record this install's sync mark", err);
  }
}

function profileRef(uid: string) {
  return doc(db, PLAYERS, uid, PRIVATE, PROFILE_DOC);
}

function setSyncState(state: SyncState): void {
  useAccountStore.getState().setSyncState(state);
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function choiceKey(uid: string): string {
  return syncChoiceKey(uid);
}

function recordedChoice(uid: string): SyncChoice | null {
  try {
    return localStorage.getItem(choiceKey(uid)) as SyncChoice | null;
  } catch {
    // No storage (private mode): the question gets asked again next session.
    // Annoying, but far better than silently merging a shared device's progress.
    return null;
  }
}

function rememberChoice(uid: string, choice: SyncChoice): void {
  try {
    localStorage.setItem(choiceKey(uid), choice);
  } catch {
    /* same */
  }
}

interface Scheduler {
  /** Queue a run: at the end of the debounce window, or straight away. */
  request(immediate: boolean): void;
  /** Drop anything pending. A run already in flight still finishes. */
  cancel(): void;
}

/**
 * Debounce and coalescing, with no Firestore in it.
 *
 * Two rules, both from §B6's "~3 writes per session" budget:
 *
 *   - the first mutation opens a fixed window rather than each one restarting
 *     it. A rolling debounce can be pushed past the end of a session by a steady
 *     trickle of updates, which is exactly the shape a long match has,
 *   - one run at a time. A trigger arriving mid-flight marks the state dirty and
 *     re-runs *once* afterwards, so a burst of triggers during a slow sync costs
 *     one extra write rather than one each.
 */
function createScheduler(delayMs: number, run: () => Promise<void>): Scheduler {
  let timer: number | undefined;
  let inFlight = false;
  let dirty = false;

  function fire(): void {
    timer = undefined;
    if (inFlight) {
      dirty = true;
      return;
    }
    inFlight = true;
    void run().finally(() => {
      inFlight = false;
      if (!dirty) return;
      dirty = false;
      fire();
    });
  }

  return {
    request(immediate) {
      if (immediate) {
        if (timer !== undefined) clearTimeout(timer);
        fire();
        return;
      }
      if (timer !== undefined) return;
      timer = window.setTimeout(fire, delayMs);
    },
    cancel() {
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
      dirty = false;
    },
  };
}

const scheduler = createScheduler(DEBOUNCE_MS, async () => {
  const uid = activeUid;
  // Re-checked here as well as at the trigger: a queued or coalesced run can
  // reach this point after the player has signed out.
  if (uid === null || readOnly) return;
  await syncNow(uid);
});
